import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import type {
  AssistantTurnData,
  ChatTurnFeedback,
  ClarificationRequest,
  ClarificationResponseResult,
  ConversationTurn,
  FirstTurnResponse,
  FollowUpTurnResponse,
  SessionTurnsResponse,
  TerminateTurnResponse,
  TurnResponseStatus,
} from "@/features/chat/model/chatTypes";
import { mapSessionTurnItemToConversationTurn } from "@/features/chat/model/chatTypes";
import { answerPartsFromStoredSteps } from "@/features/chat/lib/answerParts";
import { pauseTurnClock, resumeTurnClock } from "@/features/chat/lib/turnClock";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";
import {
  addChatTurnFeedbackComment as requestChatTurnFeedbackComment,
  setChatTurnReaction as requestChatTurnReaction,
} from "@/features/chat/lib/chatTurnFeedbackApi";
import { nextFeedbackAfterReactionClick, type Reaction } from "@/shared/lib/feedbackReaction";

export type ChatState = {
  sessionId: string | null;
  turnId: string | null;
  domainId: number | null;
  sessionTitle: string | null;
  /** Who asked a loaded session's questions; null for a chat started on this page. */
  sessionUserName: string | null;
  turns: ConversationTurn[];
  sendStatus: "idle" | "loading" | "succeeded" | "failed";
  loadStatus: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
};

export const chatInitialState: ChatState = {
  sessionId: null,
  turnId: null,
  domainId: null,
  sessionTitle: null,
  sessionUserName: null,
  turns: [],
  sendStatus: STATUS.IDLE,
  loadStatus: STATUS.IDLE,
  error: null,
};

type SendFirstTurnArgs = {
  domainId: number;
  userQuestion: string;
  clientTurnId: string;
  title?: string | null;
};

type SendFollowUpTurnArgs = {
  sessionId: string;
  userQuestion: string;
  clientTurnId: string;
};

function isFirstTurnResponse(value: unknown): value is FirstTurnResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.session_id === "string" &&
    typeof row.turn_id === "string" &&
    typeof row.turn_index === "number" &&
    row.status === "pending"
  );
}

function isFollowUpTurnResponse(value: unknown): value is FollowUpTurnResponse {
  return isFirstTurnResponse(value);
}

function isSessionTurnsResponse(value: unknown): value is SessionTurnsResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.session_id === "string" &&
    typeof row.domain_id === "number" &&
    (row.title === null || typeof row.title === "string") &&
    typeof row.created_at === "string" &&
    Array.isArray(row.turns)
  );
}

function isTerminateTurnResponse(value: unknown): value is TerminateTurnResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.turn_id === "string" && row.status === "terminated";
}

function isClarificationResponseResult(value: unknown): value is ClarificationResponseResult {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.turn_id === "string" && row.response_status === "streaming";
}

import type { ClarificationAnswerItem } from "@/features/chat/model/chatTypes";

export const submitClarificationResponse = createAsyncThunk<
  ClarificationResponseResult,
  {
    turnId: string;
    answers: ClarificationAnswerItem[];
    clientMessageId: string;
    /** Optimistic preview only — the backend re-derives this from the answers. */
    rewrittenQuestion?: string;
  },
  { rejectValue: string }
>(
  "chat/submitClarificationResponse",
  async ({ turnId, answers, clientMessageId, rewrittenQuestion }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/chat/turns/${turnId}/clarification-response`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers: answers.map((answer) => ({
            question_id: answer.question_id,
            selected_option_id: answer.selected_option_id,
            selected_option_ids: answer.selected_option_ids,
            custom_text: answer.custom_text,
          })),
          rewritten_question: rewrittenQuestion,
          client_message_id: clientMessageId,
        }),
      });

      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }

      const data: unknown = await res.json();
      if (!isClarificationResponseResult(data)) {
        return rejectWithValue("Unexpected response from chat service.");
      }

      return data;
    } catch {
      return rejectWithValue("Unable to reach the chat service.");
    }
  },
);

export const sendFirstTurn = createAsyncThunk<
  FirstTurnResponse,
  SendFirstTurnArgs,
  { rejectValue: string }
>(
  "chat/sendFirstTurn",
  async ({ domainId, userQuestion, title = null }, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/chat/sessions/first-turn", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain_id: domainId,
          title,
          user_question: userQuestion,
        }),
      });

      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }

      const data: unknown = await res.json();
      if (!isFirstTurnResponse(data)) {
        return rejectWithValue("Unexpected response from chat service.");
      }

      return data;
    } catch {
      return rejectWithValue("Unable to reach the chat service.");
    }
  },
);

export const sendFollowUpTurn = createAsyncThunk<
  FollowUpTurnResponse,
  SendFollowUpTurnArgs,
  { rejectValue: string }
>("chat/sendFollowUpTurn", async ({ sessionId, userQuestion }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/chat/sessions/${sessionId}/turns`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_question: userQuestion }),
    });

    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }

    const data: unknown = await res.json();
    if (!isFollowUpTurnResponse(data)) {
      return rejectWithValue("Unexpected response from chat service.");
    }

    return data;
  } catch {
    return rejectWithValue("Unable to reach the chat service.");
  }
});

export const loadSessionTurns = createAsyncThunk<
  SessionTurnsResponse,
  string,
  { rejectValue: string }
>("chat/loadSessionTurns", async (sessionId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/chat/sessions/${sessionId}/turns`, {
      method: "GET",
    });

    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }

    const data: unknown = await res.json();
    if (!isSessionTurnsResponse(data)) {
      return rejectWithValue("Unexpected response from chat service.");
    }

    return data;
  } catch {
    return rejectWithValue("Unable to reach the chat service.");
  }
});

export const terminateTurn = createAsyncThunk<
  TerminateTurnResponse,
  string,
  { rejectValue: string }
>("chat/terminateTurn", async (turnId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/chat/turns/${turnId}/terminate`, {
      method: "POST",
    });

    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }

    const data: unknown = await res.json();
    if (!isTerminateTurnResponse(data)) {
      return rejectWithValue("Unexpected response from chat service.");
    }

    return data;
  } catch {
    return rejectWithValue("Unable to reach the chat service.");
  }
});

type ChatTurnFeedbackResult = { turnId: string; feedback: ChatTurnFeedback };

type SetChatTurnReactionArgs = {
  turnId: string;
  reaction: Reaction;
  /** Restored verbatim if the request fails — the reducers cannot capture it themselves. */
  previousFeedback: ChatTurnFeedback | null;
};

export const setChatTurnReaction = createAsyncThunk<
  ChatTurnFeedbackResult,
  SetChatTurnReactionArgs,
  { rejectValue: string }
>("chat/setTurnReaction", async ({ turnId, reaction }, { rejectWithValue }) => {
  try {
    const feedback = await requestChatTurnReaction(turnId, reaction);
    return { turnId, feedback };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to save reaction");
  }
});

export const submitChatTurnFeedbackComment = createAsyncThunk<
  ChatTurnFeedbackResult,
  { turnId: string; comment: string },
  { rejectValue: string }
>("chat/submitTurnFeedbackComment", async ({ turnId, comment }, { rejectWithValue }) => {
  try {
    const feedback = await requestChatTurnFeedbackComment(turnId, comment);
    return { turnId, feedback };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to save comment");
  }
});

function patchTurnFeedback(
  state: ChatState,
  turnId: string,
  feedback: ChatTurnFeedback | null,
): void {
  const turn = state.turns.find((row) => row.turnId === turnId);
  if (turn) {
    turn.feedback = feedback;
  }
}

const chatSlice = createSlice({
  name: "chat",
  initialState: chatInitialState,
  reducers: {
    resetChatSession(state) {
      state.sessionId = null;
      state.turnId = null;
      state.domainId = null;
      state.sessionTitle = null;
      state.sessionUserName = null;
      state.turns = [];
      state.sendStatus = STATUS.IDLE;
      state.loadStatus = STATUS.IDLE;
      state.error = null;
    },
    addOptimisticTurn(
      state,
      action: PayloadAction<{ clientTurnId: string; userQuestion: string }>,
    ) {
      state.turns.push({
        turnId: action.payload.clientTurnId,
        turnIndex: state.turns.length + 1,
        userQuestion: action.payload.userQuestion,
        assistantData: null,
        status: "pending",
        feedback: null,
        startedAt: Date.now(),
      });
    },
    updateTurnStatus(
      state,
      action: PayloadAction<{
        turnId: string;
        status: TurnResponseStatus;
      }>,
    ) {
      const turn = state.turns.find((row) => row.turnId === action.payload.turnId);
      if (!turn) {
        return;
      }
      turn.status = action.payload.status;
      if (action.payload.status === "waiting_for_clarification") {
        pauseTurnClock(turn, Date.now());
      } else {
        resumeTurnClock(turn, Date.now());
      }
    },
    updateTurnStreamPreview(
      state,
      action: PayloadAction<{
        turnId: string;
        assistantData: AssistantTurnData;
      }>,
    ) {
      const turn = state.turns.find((row) => row.turnId === action.payload.turnId);
      if (!turn) {
        return;
      }
      turn.assistantData = action.payload.assistantData;
      turn.status = "streaming";
    },
    completeTurn(
      state,
      action: PayloadAction<{
        turnId: string;
        assistantData: AssistantTurnData;
      }>,
    ) {
      const turn = state.turns.find((row) => row.turnId === action.payload.turnId);
      if (!turn) {
        return;
      }
      turn.assistantData = action.payload.assistantData;
      turn.status = "complete";
      turn.completedAt ??= Date.now();
    },
    terminateTurnLocally(
      state,
      action: PayloadAction<{
        turnId: string;
        assistantData: AssistantTurnData;
      }>,
    ) {
      const turn = state.turns.find((row) => row.turnId === action.payload.turnId);
      if (!turn) {
        return;
      }
      turn.assistantData = action.payload.assistantData;
      turn.status = "terminated";
      turn.completedAt ??= Date.now();
      state.error = null;
    },
    removeOptimisticTurn(state, action: PayloadAction<{ clientTurnId: string }>) {
      state.turns = state.turns.filter((row) => row.turnId !== action.payload.clientTurnId);
    },
    setTurnClarificationRequest(
      state,
      action: PayloadAction<{
        turnId: string;
        clarificationRequest: ClarificationRequest;
      }>,
    ) {
      const turn = state.turns.find((row) => row.turnId === action.payload.turnId);
      if (!turn) {
        return;
      }
      turn.status = "waiting_for_clarification";
      pauseTurnClock(turn, Date.now());
      turn.clarificationRequest = action.payload.clarificationRequest;
      if (turn.assistantData) {
        turn.assistantData.clarificationRequest = action.payload.clarificationRequest;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendFirstTurn.pending, (state) => {
        state.sendStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(sendFirstTurn.fulfilled, (state, action) => {
        state.sendStatus = STATUS.SUCCEEDED;
        state.sessionId = action.payload.session_id;
        state.turnId = action.payload.turn_id;
        state.domainId = action.meta.arg.domainId;
        const turn = state.turns.find((row) => row.turnId === action.meta.arg.clientTurnId);
        if (turn) {
          turn.turnId = action.payload.turn_id;
          turn.turnIndex = action.payload.turn_index;
          turn.status = "streaming";
        }
      })
      .addCase(sendFirstTurn.rejected, (state, action) => {
        state.sendStatus = STATUS.FAILED;
        state.error = action.payload ?? "Failed to send message.";
      })
      .addCase(sendFollowUpTurn.pending, (state) => {
        state.sendStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(sendFollowUpTurn.fulfilled, (state, action) => {
        state.sendStatus = STATUS.SUCCEEDED;
        state.sessionId = action.payload.session_id;
        state.turnId = action.payload.turn_id;
        const turn = state.turns.find((row) => row.turnId === action.meta.arg.clientTurnId);
        if (turn) {
          turn.turnId = action.payload.turn_id;
          turn.turnIndex = action.payload.turn_index;
          turn.status = "streaming";
        }
      })
      .addCase(sendFollowUpTurn.rejected, (state, action) => {
        state.sendStatus = STATUS.FAILED;
        state.error = action.payload ?? "Failed to send message.";
      })
      .addCase(loadSessionTurns.pending, (state) => {
        state.loadStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(loadSessionTurns.fulfilled, (state, action) => {
        state.loadStatus = STATUS.SUCCEEDED;
        state.sessionId = action.payload.session_id;
        state.domainId = action.payload.domain_id;
        state.sessionTitle = action.payload.title;
        state.sessionUserName = action.payload.user_name ?? null;
        state.turns = action.payload.turns.map((item) => {
          const turn = mapSessionTurnItemToConversationTurn(item);
          const inProgress =
            turn.status === "pending" ||
            turn.status === "streaming" ||
            turn.status === "waiting_for_clarification";
          return inProgress
            ? { ...turn, resumeAnswerParts: answerPartsFromStoredSteps(item.verbose_steps ?? []) }
            : turn;
        });
      })
      .addCase(loadSessionTurns.rejected, (state, action) => {
        state.loadStatus = STATUS.FAILED;
        state.error = action.payload ?? "Failed to load conversation.";
      })
      .addCase(terminateTurn.fulfilled, (state, action) => {
        state.sendStatus = STATUS.SUCCEEDED;
        state.error = null;
        const turn = state.turns.find((row) => row.turnId === action.payload.turn_id);
        if (turn) {
          turn.status = "terminated";
          turn.completedAt ??= Date.now();
        }
      })
      .addCase(submitClarificationResponse.pending, (state) => {
        state.sendStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(submitClarificationResponse.fulfilled, (state, action) => {
        state.sendStatus = STATUS.SUCCEEDED;
        state.turnId = action.payload.turn_id;
        const turn = state.turns.find((row) => row.turnId === action.payload.turn_id);
        if (turn) {
          turn.status = "streaming";
          turn.clarificationRequest = null;
          resumeTurnClock(turn, Date.now());
        }
      })
      .addCase(submitClarificationResponse.rejected, (state, action) => {
        state.sendStatus = STATUS.FAILED;
        state.error = action.payload ?? "Failed to submit clarification.";
      })
      // Paint the thumb on click, then reconcile with whatever the server actually stored.
      .addCase(setChatTurnReaction.pending, (state, action) => {
        const { turnId, reaction, previousFeedback } = action.meta.arg;
        patchTurnFeedback(state, turnId, nextFeedbackAfterReactionClick(previousFeedback, reaction));
      })
      .addCase(setChatTurnReaction.fulfilled, (state, action) => {
        patchTurnFeedback(state, action.payload.turnId, action.payload.feedback);
      })
      .addCase(setChatTurnReaction.rejected, (state, action) => {
        patchTurnFeedback(state, action.meta.arg.turnId, action.meta.arg.previousFeedback);
      })
      .addCase(submitChatTurnFeedbackComment.fulfilled, (state, action) => {
        patchTurnFeedback(state, action.payload.turnId, action.payload.feedback);
      });
  },
});

export const {
  resetChatSession,
  addOptimisticTurn,
  updateTurnStatus,
  updateTurnStreamPreview,
  completeTurn,
  terminateTurnLocally,
  removeOptimisticTurn,
  setTurnClarificationRequest,
} = chatSlice.actions;
export const chatReducer = chatSlice.reducer;
